import React, { useState, useContext, createContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import MediaRender from '../MediaRender';
import Button from './Button';
import Input from './Input';
import Logo from '../Logo';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement?.classList?.toggle('dark', newTheme === 'dark');
  };

  React.useEffect(() => {
    document.documentElement?.classList?.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const UserAvatarMenu = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const handleToggle = () => setIsOpen(!isOpen);
  const handleClose = () => setIsOpen(false);

  const handleLogout = () => {
    handleClose();
    onLogout();
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="flex items-center space-x-2 p-1 rounded-full hover:bg-muted transition-colors duration-150 ease-out"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-primary flex items-center justify-center">
          {user?.avatar ? (
            <MediaRender
              type="image"
              src={user?.avatar}
              alt={`${user?.name || 'User'} avatar`}
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon name="User" size={18} color="white" />
          )}
        </div>
        <Icon 
          name="ChevronDown" 
          size={16} 
          className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={handleClose}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevation-2 z-50 animate-slide-up">
            <div className="p-3 border-b border-border">
              <p className="font-medium text-sm text-foreground">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                @{user?.username || 'username'}
              </p>
            </div>
            
            <div className="py-1">
              <Link
                to="/user-profile"
                className="flex items-center px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                onClick={handleClose}
              >
                <Icon name="User" size={16} className="mr-3" />
                Profile
              </Link>
              
              <button
                onClick={toggleTheme}
                className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              >
                <Icon 
                  name={theme === 'light' ? 'Moon' : 'Sun'} 
                  size={16} 
                  className="mr-3" 
                />
                {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </button>
              
              <div className="border-t border-border my-1" />
              
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors duration-150"
              >
                <Icon name="LogOut" size={16} className="mr-3" />
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SearchInput = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      window.location.href = `/search-results?q=${encodeURIComponent(searchQuery?.trim())}`;
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e?.target?.value);
  };

  return (
    <div className="hidden md:flex items-center bg-white/5 border border-white/10 rounded-full px-4 py-2 gap-3 focus-within:border-white/40 transition-all">
      <form onSubmit={handleSearch} className="flex items-center w-full">
        <Icon 
          name="Search" 
          size={20} 
          className="text-primary/50"
        />
        <input
          type="search"
          placeholder="Explore..."
          value={searchQuery}
          onChange={handleInputChange}
          className="bg-transparent border-none focus:ring-0 text-ui-button text-primary p-0 placeholder:text-primary/30 w-48 ml-3 outline-none"
        />
      </form>
    </div>
  );
};

const ThemePicker = () => {
  const [isOpen, setIsOpen] = useState(false);

  const themes = [
    { name: 'black', label: 'Aura', color: '#131314', border: 'rgba(255,255,255,0.2)' },
    { name: 'slate', label: 'Slate', color: '#2c3e50', border: 'rgba(255,255,255,0.2)' },
    { name: 'white', label: 'Light', color: '#ffffff', border: 'rgba(0,0,0,0.15)' },
    { name: 'green', label: 'Forest', color: '#2d3436', border: 'rgba(255,255,255,0.2)' },
  ];

  const setTheme = (themeName) => {
    const body = document.body;
    const glow = document.getElementById('glow-orb');
    body.classList.remove('theme-black', 'theme-slate', 'theme-white', 'theme-green');
    body.classList.add(`theme-${themeName}`);

    // Update body background + glow orb colors
    const t = themes.find(t => t.name === themeName);
    if (t) body.style.backgroundColor = t.color;
    if (glow) {
      switch(themeName) {
        case 'black':
          glow.style.background = 'linear-gradient(to right, #3b82f6, #ffffff, #fb923c)';
          glow.style.opacity = '0.2'; break;
        case 'slate':
          glow.style.background = 'linear-gradient(to right, #60a5fa, #94a3b8, #334155)';
          glow.style.opacity = '0.25'; break;
        case 'white':
          glow.style.background = 'linear-gradient(to right, #cbd5e1, #ffffff, #e2e8f0)';
          glow.style.opacity = '0.4'; break;
        case 'green':
          glow.style.background = 'linear-gradient(to right, #4ade80, #166534, #064e3b)';
          glow.style.opacity = '0.15'; break;
      }
    }

    localStorage.setItem('aura-theme', themeName);
    setIsOpen(false);
  };

  // Load saved theme on mount
  React.useEffect(() => {
    const saved = localStorage.getItem('aura-theme');
    if (saved) {
      document.body.classList.remove('theme-black', 'theme-slate', 'theme-white', 'theme-green');
      document.body.classList.add(`theme-${saved}`);
    }
  }, []);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition-all active:scale-95"
        title="Change Theme"
      >
        <Icon name="Palette" size={18} className="text-primary" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-3 right-0 z-50 glass-panel rounded-xl p-4 w-48 animate-slide-up">
            <p className="font-label-caps text-label-caps text-primary/50 tracking-widest mb-3">APPEARANCE</p>
            <div className="grid grid-cols-2 gap-3">
              {themes.map((t) => (
                <button
                  key={t.name}
                  onClick={() => setTheme(t.name)}
                  className="group flex flex-col items-center gap-1.5"
                >
                  <div
                    className="w-full h-8 rounded-lg cursor-pointer transition-all hover:scale-105 active:scale-95 ring-1"
                    style={{ background: t.color, borderColor: t.border, '--tw-ring-color': t.border }}
                  />
                  <span className="text-[9px] font-label-caps text-primary/40 group-hover:text-primary/70 transition-colors tracking-wider">
                    {t.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Header = ({ user, onLogout, notificationCount = 0 }) => {
  const location = useLocation();
  
  const navigationItems = [
    { path: '/news-feed', label: 'Feed', icon: 'Home', color: 'text-purple-400' },
    { path: '/search-results', label: 'Directory', icon: 'Search', color: 'text-blue-400' },
    { path: '/user-profile', label: 'Profile', icon: 'User', color: 'text-amber-400' },
    { path: '/admin', label: 'Admin', icon: 'Shield', color: 'text-emerald-400' }
  ];

  const isActivePath = (path) => {
    if (path === '/news-feed' && location?.pathname === '/') return true;
    if (path === '/admin' && location?.pathname?.startsWith('/admin')) return true;
    return location?.pathname === path;
  };

  return (
    <>
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[92%] max-w-7xl z-50 glass-overlay border border-white/10 rounded-full px-6 py-3 shadow-2xl flex justify-between items-center transition-all duration-300">
        {/* Brand Logo with soft glow */}
        <Link to="/" className="flex items-center group">
          <Logo />
        </Link>

        {/* Center: Sleek navigation links */}
        <div className="hidden md:flex items-center gap-1.5 bg-black/20 border border-white/5 rounded-full px-2 py-1">
          {navigationItems?.map((item) => {
            const active = isActivePath(item?.path);
            return (
              <Link
                key={item?.path}
                to={item?.path}
                className={`relative flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                  active 
                    ? 'text-zinc-100 bg-white/10 shadow-sm border border-white/5' 
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                <Icon name={item?.icon} size={14} className={item?.color} />
                <span>{item?.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Right: Quick Settings & User Profiles */}
        <div className="flex items-center gap-3.5">
          <SearchInput />
          <ThemePicker />
          <UserAvatarMenu user={user} onLogout={onLogout} />
        </div>
      </nav>
      
      {/* Bottom NavBar (Mobile) */}
      <div className="md:hidden fixed bottom-6 left-0 right-0 z-50 flex justify-around items-center px-4">
        <div className="bg-[#050508]/90 backdrop-blur-2xl border border-white/10 rounded-full w-[90%] max-w-md flex justify-around items-center py-2.5 px-3 shadow-2xl">
          {navigationItems?.map((item) => {
            const active = isActivePath(item?.path);
            return (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all active:scale-95 ${
                  active 
                    ? 'text-purple-400 bg-white/5 border border-purple-500/20' 
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-sans text-[9px] mt-1 font-semibold">{item?.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Header;